import type { FileCategory, JobRecord } from '../core/types';
import type { useHistory } from '../app/hooks/useHistory';
import { fidelityLabel } from '../core/fidelity';
import { formatBytes } from './TaskPanel';

export interface HistoryViewProps {
  history: ReturnType<typeof useHistory>;
  /** 产物仅存在于本次会话内存时才能重新下载，由工作台提供。 */
  onReDownload?: (record: JobRecord) => void;
}

const CATEGORY_LABELS: Record<FileCategory | 'all', string> = {
  all: '全部分类',
  image: '图片',
  document: '文档',
  model: '3D',
  archive: '压缩包',
  other: '其他',
};

export function HistoryView({ history, onReDownload }: HistoryViewProps) {
  return (
    <div className="stack" style={{ paddingBottom: 40 }}>
      <div className="card">
        <div className="card-head">
          <span className="card-title">转换记录</span>
          <span className="spacer" />
          <span className="card-hint">共 {history.total} 条 · 只保存元数据与缩略图</span>
        </div>

        <div className="row" style={{ flexWrap: 'wrap' }}>
          <select
            className="select"
            style={{ maxWidth: 160 }}
            aria-label="按分类筛选"
            value={history.filters.category}
            onChange={(event) => history.updateFilters({ category: event.target.value as FileCategory | 'all' })}
          >
            {(Object.keys(CATEGORY_LABELS) as Array<FileCategory | 'all'>).map((key) => (
              <option key={key} value={key}>
                {CATEGORY_LABELS[key]}
              </option>
            ))}
          </select>

          <select
            className="select"
            style={{ maxWidth: 160 }}
            aria-label="按状态筛选"
            value={history.filters.status}
            onChange={(event) => history.updateFilters({ status: event.target.value as JobRecord['status'] | 'all' })}
          >
            <option value="all">全部状态</option>
            <option value="completed">成功</option>
            <option value="failed">失败</option>
            <option value="cancelled">已取消</option>
          </select>

          <input
            className="input"
            style={{ maxWidth: 240 }}
            type="search"
            placeholder="搜索文件名或格式"
            value={history.filters.keyword}
            onChange={(event) => history.updateFilters({ keyword: event.target.value })}
          />

          <span className="spacer" />
          <button type="button" className="btn" onClick={() => void history.refresh()} disabled={history.loading}>
            {history.loading ? '读取中…' : '刷新'}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={history.total === 0}
            onClick={() => {
              if (window.confirm('确定清空全部转换记录？该操作不可撤销（不影响你的文件）。')) void history.clearAll();
            }}
          >
            清空记录
          </button>
        </div>
      </div>

      <div className="card">
        {history.records.length === 0 ? (
          <div className="empty">
            <div style={{ marginBottom: 8 }}>还没有转换记录</div>
            <span className="card-hint">转换完成后会自动留档，便于回看参数与保真度结论。</span>
          </div>
        ) : (
          <div className="list">
            {history.records.map((record) => (
              <button type="button" key={record.id} className="list-item" onClick={() => history.setSelected(record)}>
                {record.thumbnailDataUrl ? (
                  <img className="thumb" src={record.thumbnailDataUrl} alt="" />
                ) : (
                  <span className="thumb-fallback">{record.sourceFormat.slice(0, 4).toUpperCase()}</span>
                )}
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span className="row">
                    <span className="file-name">{record.sourceName}</span>
                    <span className="badge" data-tone="muted">
                      {record.sourceFormat.toUpperCase()} → {record.targetFormat.toUpperCase()}
                    </span>
                  </span>
                  <span className="file-meta">
                    <span>{formatBytes(record.sourceSize)}</span>
                    <span>→</span>
                    <span>{record.outputSize ? formatBytes(record.outputSize) : '—'}</span>
                    <span>·</span>
                    <span>{new Date(record.createdAt).toLocaleString('zh-CN')}</span>
                    <span>·</span>
                    <span>{record.durationMs}ms</span>
                  </span>
                </span>
                <span className="stack" style={{ gap: 6, alignItems: 'flex-end' }}>
                  <span className="badge" data-level={record.fidelityLevel}>
                    {fidelityLabel(record.fidelityLevel)}
                  </span>
                  <span className="badge" data-tone={record.status === 'completed' ? 'ok' : record.status === 'failed' ? 'muted' : 'muted'}>
                    {record.status === 'completed' ? '成功' : record.status === 'failed' ? '失败' : '已取消'}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}

        {history.pageCount > 1 ? (
          <div className="row" style={{ marginTop: 14, justifyContent: 'center' }}>
            <button type="button" className="btn" disabled={history.page === 0} onClick={() => history.setPage(history.page - 1)}>
              上一页
            </button>
            <span className="card-hint">
              第 {history.page + 1} / {history.pageCount} 页
            </span>
            <button
              type="button"
              className="btn"
              disabled={history.page + 1 >= history.pageCount}
              onClick={() => history.setPage(history.page + 1)}
            >
              下一页
            </button>
          </div>
        ) : null}
      </div>

      {history.selected ? (
        <div className="overlay" onClick={() => history.setSelected(null)} role="presentation">
          <aside className="drawer" onClick={(event) => event.stopPropagation()} role="dialog" aria-label="记录详情">
            <div className="drawer-head">
              <span className="card-title">记录详情</span>
              <span className="spacer" />
              <button type="button" className="btn btn-ghost" onClick={() => history.setSelected(null)} aria-label="关闭">
                ✕
              </button>
            </div>

            <div className="detail-grid">
              <DetailRow label="源文件" value={history.selected.sourceName} />
              <DetailRow label="转换" value={`${history.selected.sourceFormat.toUpperCase()} → ${history.selected.targetFormat.toUpperCase()}`} />
              <DetailRow label="引擎" value={history.selected.engine} />
              <DetailRow
                label="模式"
                value={
                  history.selected.mode === 'ai'
                    ? 'AI 识别（内容上传）'
                    : history.selected.mode === 'cloud'
                      ? '云端'
                      : '本地（不上传）'
                }
              />
              <DetailRow label="体积" value={`${formatBytes(history.selected.sourceSize)} → ${history.selected.outputSize ? formatBytes(history.selected.outputSize) : '—'}`} />
              <DetailRow label="耗时" value={`${history.selected.durationMs} ms`} />
              <DetailRow label="保真度" value={`${fidelityLabel(history.selected.fidelityLevel)} · ${history.selected.fidelitySummary}`} />
              <DetailRow label="状态" value={history.selected.status} />
              {history.selected.errorCode ? <DetailRow label="错误码" value={history.selected.errorCode} /> : null}
              <DetailRow
                label="指纹"
                value={`${history.selected.sourceHash.slice(0, 24)}…${history.selected.sourceHashPartial ? '（部分哈希）' : ''}`}
              />
              {history.selected.duplicateOf ? <DetailRow label="重复来源" value="与既有记录同源，缩略图已复用" /> : null}
            </div>

            <div className="row" style={{ marginTop: 16, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn"
                disabled={!onReDownload}
                title={onReDownload ? undefined : '产物不落盘，仅本次会话内可重新下载'}
                onClick={() => onReDownload?.(history.selected!)}
              >
                重新下载
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void history.remove(history.selected!.id)}
              >
                删除该记录
              </button>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="detail-row">
      <span className="key">{label}</span>
      <span style={{ textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}
